import React, { useState } from 'react'
import { Header } from '../components/Header'
import { Footer } from '../components/Footer'
import { contactInfo } from '../data/mockData'
import { motion } from 'framer-motion'
import { SeoHelmet } from '../components/SeoHelmet'
import { ROUTE_META } from '../data/routeMeta'

export function Contact() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [service, setService] = useState('Product Building')
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')
    setErrorMsg('')
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, service, message }),
      })
      const data = await res.json()
      if (data.ok) {
        setStatus('success')
        setName('')
        setEmail('')
        setService('Product Building')
        setMessage('')
      } else {
        setStatus('error')
        setErrorMsg(data.error || 'Failed to send message')
      }
    } catch {
      setStatus('error')
      setErrorMsg('Network error. Please try again.')
    }
  }

  return (
    <div className="min-h-screen w-full bg-white flex flex-col font-['Roboto']">
      <SeoHelmet {...ROUTE_META['/contact']} url="/contact" pageType="website" />
      <Header />

      <main className="flex-1">
        <section className="relative w-full bg-navy py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-transparent" />
          </div>

          <div className="max-w-5xl mx-auto relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white font-['Poppins']">
                Get in <span className="text-primary">touch</span>
              </h1>
              <p className="text-xl sm:text-2xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Ready to transform your workflow with AI? Book a consultation or
                send us a message to get started.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-8 font-['Poppins']">
                  Let's talk about your project
                </h2>
                <p className="text-lg text-gray-600 mb-12">
                  Whether you need a full product build, strategic consulting on
                  data privacy, or training for your team, we're here to help
                  you navigate the AI landscape safely and effectively.
                </p>

                <div className="space-y-8">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-light-background flex items-center justify-center text-primary mr-6">
                        {info.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 font-['Poppins']">
                          {info.title}
                        </h3>
                        <p className="text-gray-600">{info.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-white rounded-2xl p-8 shadow-md border border-gray-100"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6 font-['Poppins']">
                  Send a message
                </h3>
                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => { setName(e.target.value); setStatus('idle') }}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setStatus('idle') }}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors"
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Service of interest
                    </label>
                    <select
                      id="service"
                      value={service}
                      onChange={(e) => { setService(e.target.value); setStatus('idle') }}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors bg-white"
                    >
                      <option>Product Building</option>
                      <option>Consulting</option>
                      <option>Training</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={4}
                      value={message}
                      onChange={(e) => { setMessage(e.target.value); setStatus('idle') }}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-colors resize-none"
                      placeholder="How can we help you?"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full py-4 px-6 text-white bg-primary hover:bg-primary-dark disabled:bg-primary/50 rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                  >
                    {status === 'loading' ? 'Sending...' : 'Send Message'}
                  </button>
                  {status === 'success' && (
                    <p className="text-green-600 text-center font-medium">
                      Thank you, we'll be in touch.
                    </p>
                  )}
                  {status === 'error' && (
                    <p className="text-red-600 text-center font-medium">
                      {errorMsg}
                    </p>
                  )}
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}